/**
 * Resale Pricing → Overview tab: an educational walk-through of how Trove
 * turns a partner's feed into the storefront price. Two zones mirror the data
 * model — the catalog item (shared record: anchor price → base / override /
 * active price) and the inventory item (physical unit: condition discount) —
 * and every configurable stage links to its tab. All dollar amounts are
 * illustrative, as the callout at the top states.
 */
import { useState } from 'react';
import { Title } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import {
  alpha,
  Alert,
  Box,
  Chip,
  Collapse,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExposureIcon from '@mui/icons-material/Exposure';

import { useTitle } from '@src/hooks/useTitle';
import { SectionLabel } from '@src/components/indexPage';
import { ResalePricingTabShell } from './shared';

/** 10.5px small-caps voice used for card sublabels — same as KeyFactsStrip labels. */
const CAPS = {
  fontSize: 10.5,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: 'text.secondary',
} as const;

const MONEY = { fontVariantNumeric: 'tabular-nums', fontWeight: 700 } as const;

/** Vertical flow line with an optional explanatory caption between zones. */
const Connector = ({ children }: { children?: React.ReactNode }) => (
  <Stack alignItems="center" spacing={children ? 1 : 0} sx={{ py: children ? 1 : 0.5 }}>
    <Box sx={{ width: '1px', height: 14, bgcolor: 'divider' }} />
    {children && (
      <>
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', px: 2 }}>
          {children}
        </Typography>
        <Box sx={{ width: '1px', height: 14, bgcolor: 'divider' }} />
      </>
    )}
  </Stack>
);

const Strong = ({ children }: { children: React.ReactNode }) => (
  <Box component="strong" sx={{ color: 'text.primary', fontWeight: 600 }}>
    {children}
  </Box>
);

interface FlowCardProps {
  label: string;
  value: string;
  subLabel?: string;
  /** Link target — makes the whole card clickable with hover lift. */
  to?: string;
  linkLabel?: string;
  /** Non-link footer note (e.g. "Contact Trove to update"). */
  footnote?: string;
  tone?: 'default' | 'highlight' | 'muted';
  badge?: string;
}

/**
 * One stage in the pricing flow. Highlighted cards use the rust accent;
 * clickable cards lift on hover and slide their › arrow (microanimation).
 */
const FlowCard = ({ label, value, subLabel, to, linkLabel, footnote, tone = 'default', badge }: FlowCardProps) => {
  const isLink = Boolean(to);
  const highlight = tone === 'highlight';

  const body = (
    <>
      {badge && (
        <Chip
          label={badge}
          size="small"
          sx={{
            position: 'absolute',
            top: -11,
            left: '50%',
            transform: 'translateX(-50%)',
            height: 20,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '.04em',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        />
      )}
      <Typography variant="caption" sx={{ color: highlight ? 'primary.dark' : 'text.secondary' }}>
        {label}
      </Typography>
      <Typography sx={{ ...MONEY, fontSize: 22, color: highlight ? 'primary.dark' : 'text.primary', my: 0.25 }}>
        {value}
      </Typography>
      {subLabel && (
        <Typography sx={{ ...CAPS, color: highlight ? 'primary.dark' : 'text.secondary' }}>
          {subLabel}
        </Typography>
      )}
      {isLink && linkLabel && (
        <Typography variant="caption" sx={{ color: 'primary.dark', fontWeight: 600, mt: 1, display: 'block' }}>
          {linkLabel}
          {' '}
          <Box component="span" className="flowArrow" sx={{ display: 'inline-block', transition: 'transform 150ms ease' }}>
            ›
          </Box>
        </Typography>
      )}
      {footnote && (
        <Typography variant="caption" sx={{ color: 'primary.dark', mt: 1, display: 'block' }}>
          {footnote}
        </Typography>
      )}
    </>
  );

  const baseSx = {
    position: 'relative',
    display: 'block',
    textAlign: 'center',
    textDecoration: 'none',
    px: 2.5,
    py: 2,
    minWidth: 170,
    border: '1px solid',
    borderColor: highlight ? 'primary.main' : 'divider',
    borderRadius: 1,
    bgcolor: (theme: { palette: { primary: { main: string } } }) => {
      if (highlight) return alpha(theme.palette.primary.main, 0.06);
      return tone === 'muted' ? 'grey.50' : 'background.paper';
    },
  } as const;

  if (!isLink) return <Box sx={baseSx}>{body}</Box>;

  return (
    <Box
      component={RouterLink}
      to={to as string}
      sx={{
        ...baseSx,
        transition: 'transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 6px 16px rgba(31, 41, 55, 0.12)',
          borderColor: 'primary.main',
        },
        '&:hover .flowArrow': { transform: 'translateX(3px)' },
      }}
    >
      {body}
    </Box>
  );
};

/** "or" between the mutually-exclusive price modes. */
const OrDivider = () => (
  <Typography variant="body2" sx={{ color: 'text.disabled', alignSelf: 'center', px: 0.5 }}>
    or
  </Typography>
);

// Illustrative condition-grade walkdown from the $45.00 example price.
const EXAMPLE_PRICE = 45;
const GRADE_DISCOUNTS: [string, number][] = [
  ['Grade A', 10],
  ['Grade B', 20],
  ['Grade C', 30],
  ['Grade D', 40],
  ['Grade E', 50],
  ['Grade F', 100],
];

const usd = (value: number) => `$${value.toFixed(2)}`;

const OverviewPage = () => {
  const { title } = useTitle('Resale Pricing');
  const [gradesOpen, setGradesOpen] = useState(true);

  return (
    <ResalePricingTabShell tab="overview">
      <Title title={title} />
      <Stack spacing={2} sx={{ mt: 1.5, maxWidth: 860 }}>
        <Box>
          <Typography variant="h6" sx={{ fontSize: 17 }}>
            How a resale price is calculated
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            How Trove turns a partner&apos;s feed into the price a shopper sees. Read top to bottom.
          </Typography>
        </Box>

        <Alert severity="warning" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ py: 0.25 }}>
          Every dollar amount below is an illustrative example — not a live price.
        </Alert>

        {/* Zone 1 — the shared catalog record */}
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 1 }}>
          <SectionLabel>Catalog item · One shared record</SectionLabel>

          {/* Source prices from the partner feed */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mt: 1 }}
          >
            <FlowCard label="MSRP" value="$120" />
            <FlowCard label="Retail" value="$110" />
            <FlowCard label="Outlet" value="$95" subLabel="Lowest" tone="highlight" />
          </Stack>

          <Connector>
            The lowest of the three becomes the
            {' '}
            <Strong>anchor price</Strong>
          </Connector>

          <Stack alignItems="center">
            <FlowCard label="Anchor price" value="$95.00" tone="muted" />
          </Stack>

          <Connector>
            Each of these is set on its own page —
            {' '}
            <Strong>click a card to open it</Strong>
            .
          </Connector>

          {/* The three price modes, best-wins */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            justifyContent="center"
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <FlowCard
              label="Base resale price"
              value="$57.00"
              subLabel="Priority 3 · Fallback"
              to="/resalePricing/pricingRules"
              linkLabel="Set in price rules"
            />
            <OrDivider />
            <FlowCard
              label="Override price"
              value="$49.00"
              subLabel="Priority 2 · When set"
              to="/resalePricing/itemAdjustedPrices"
              linkLabel="Set in overrides"
            />
            <OrDivider />
            <FlowCard
              label="Active price"
              value="$45.00"
              subLabel="Priority 1 · Highest"
              tone="highlight"
              badge="IN USE"
              footnote="Contact Trove to update"
            />
          </Stack>

          <Connector>
            Trove uses the
            {' '}
            <Strong>active price</Strong>
            {' '}
            if on, then the
            {' '}
            <Strong>override price</Strong>
            {' '}
            when set, otherwise the
            {' '}
            <Strong>base resale price</Strong>
            .
          </Connector>

          <Stack alignItems="center">
            <Box
              sx={{
                px: 2.5,
                py: 2,
                minWidth: 200,
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: 1,
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="caption" sx={{ color: 'primary.dark' }}>
                Pre-condition discount price
              </Typography>
              <Typography sx={{ ...MONEY, fontSize: 22, mt: 0.25 }}>
                $45.00
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Zone 2 — the physical unit */}
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              <SectionLabel>Inventory item · One physical unit</SectionLabel>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: -0.5 }}>
                Each physical unit is then discounted by its condition grade to give the price shown
                on its storefront page.
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
              <Link
                component={RouterLink}
                to="/resalePricing/conditionDiscounts"
                underline="hover"
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'primary.dark',
                  whiteSpace: 'nowrap',
                  '&:hover .flowArrow': { transform: 'translateX(3px)' },
                }}
              >
                Set condition discount rules
                {' '}
                <Box component="span" className="flowArrow" sx={{ display: 'inline-block', transition: 'transform 150ms ease' }}>
                  ›
                </Box>
              </Link>
              <IconButton
                size="small"
                onClick={() => setGradesOpen(open => !open)}
                aria-label={gradesOpen ? 'Collapse condition table' : 'Expand condition table'}
              >
                {gradesOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Stack>
          </Stack>

          <Collapse in={gradesOpen}>
            <Table size="small" sx={{ mt: 1.5 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Condition</TableCell>
                  <TableCell>Condition discount</TableCell>
                  <TableCell>Inventory item price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {GRADE_DISCOUNTS.map(([grade, discount]) => (
                  <TableRow key={grade}>
                    <TableCell>{grade}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{`−${discount}%`}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      {usd(EXAMPLE_PRICE * (1 - discount / 100))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </Paper>

        {/* Last step — rounding */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'grey.50',
            px: 2,
            py: 1.5,
          }}
        >
          <ExposureIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.25 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            <Strong>One last step: rounding.</Strong>
            {' '}
            Before a price reaches the storefront, Trove rounds it to your program&apos;s configured
            endings — so a calculated $31.50 might list as $31.95 or $30.99 depending on your setup.
            {' '}
            <Link
              component={RouterLink}
              to="/resalePricing/roundingRules"
              underline="hover"
              sx={{ fontWeight: 600, color: 'primary.dark', '&:hover .flowArrow': { transform: 'translateX(3px)' } }}
            >
              See rounding rules
              {' '}
              <Box component="span" className="flowArrow" sx={{ display: 'inline-block', transition: 'transform 150ms ease' }}>
                ›
              </Box>
            </Link>
          </Typography>
        </Stack>
      </Stack>
    </ResalePricingTabShell>
  );
};

export default OverviewPage;
